// SCR-ADM-FRM-2 — form başvuruları (submissions).
//
// DataTable + durum/tarih filtreleri + detay Drawer (DataJson alanlarını formun field
// label'larıyla eşleştirir; Quarantined rozetli) + durum güncelleme + xlsx/pdf export
// (blob indirme, AuditLogPage deseni). Form şeması (label eşlemesi + başlık) form.view
// gerektirir; yalnız form.submissions'lı kullanıcıda 403 → ham key'lere zarifçe düşer.

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  DatePicker,
  Drawer,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import type { TableColumnType } from 'antd';
import { ArrowLeft, Download, Eye, Paperclip } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { DataTable } from '@shared/components/DataTable';
import { ROUTES } from '@shared/constants/routes';
import { formsApi } from '@api/forms';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { SUBMISSION_STATUSES, SUBMISSION_STATUS_FLOW, type ExportFormat, type SubmissionStatus } from '../constants';
import type { FormSubmissionListItem } from '../types';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_COLOR: Record<SubmissionStatus, string> = {
  New: 'blue',
  Read: 'geekblue',
  Processed: 'green',
  Quarantined: 'red',
};

export function FormSubmissionsPage() {
  const { t } = useTranslation(['forms', 'common']);
  const td = t as unknown as (key: string) => string;
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [status, setStatus] = useState<SubmissionStatus | undefined>(undefined);
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);

  // Form şeması: başlık + field label eşlemesi (form.view). 403/hata → ham key fallback.
  const formQ = useQuery({
    queryKey: ['form', id],
    queryFn: () => formsApi.get(id),
    enabled: !!id,
    retry: false,
  });
  const fieldLabels = useMemo(
    () => new Map((formQ.data?.fields ?? []).map((f) => [f.key, f.label])),
    [formQ.data],
  );

  const query = useMemo(
    () => ({
      status,
      from: range?.[0]?.toISOString(),
      to: range?.[1]?.toISOString(),
      page,
      pageSize,
    }),
    [status, range, page, pageSize],
  );

  const listQ = useQuery({
    queryKey: ['form-submissions', id, query],
    queryFn: () => formsApi.submissions.list(id, query),
    enabled: !!id,
    placeholderData: (prev) => prev,
  });

  const detailQ = useQuery({
    queryKey: ['form-submission', id, detailId],
    queryFn: () => formsApi.submissions.get(id, detailId ?? ''),
    enabled: !!detailId,
  });

  const statusMutation = useMutation({
    mutationFn: (vars: { submissionId: string; next: SubmissionStatus }) =>
      formsApi.submissions.setStatus(id, vars.submissionId, vars.next),
    onSuccess: async () => {
      void message.success(t('submissions.statusUpdate.done'));
      await qc.invalidateQueries({ queryKey: ['form-submissions', id] });
      await qc.invalidateQueries({ queryKey: ['form-submission', id] });
    },
    onError: (err) => void message.error(resolveApiErrorMessage(err)),
  });

  const handleExport = async (format: ExportFormat) => {
    setExporting(true);
    try {
      const blob = await formsApi.submissions.export(id, {
        format,
        status,
        from: query.from,
        to: query.to,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `form-submissions-${dayjs().format('YYYYMMDD-HHmmss')}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      void message.success(t('submissions.export.done'));
    } catch (err) {
      void message.error(resolveApiErrorMessage(err) || t('submissions.export.failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadFile = async (fileId: string, index: number) => {
    if (!detailId) return;
    setDownloadingFileId(fileId);
    try {
      const blob = await formsApi.submissions.downloadFile(id, detailId, fileId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `basvuru-eki-${index + 1}${extFromMime(blob.type)}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      void message.error(resolveApiErrorMessage(err) || t('submissions.detail.downloadFailed'));
    } finally {
      setDownloadingFileId(null);
    }
  };

  const columns: TableColumnType<FormSubmissionListItem>[] = [
    {
      title: t('submissions.columns.submittedAt'),
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      width: 180,
      render: (v: string) => (v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '—'),
    },
    {
      title: t('submissions.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (s: SubmissionStatus) => <Tag color={STATUS_COLOR[s]}>{td(`submissions.status.${s}`)}</Tag>,
    },
    {
      title: t('submissions.columns.attachment'),
      dataIndex: 'hasAttachment',
      key: 'hasAttachment',
      width: 90,
      align: 'center',
      render: (has: boolean) =>
        has ? (
          <Paperclip size={15} aria-label={t('submissions.hasAttachment')} />
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      align: 'right',
      width: 90,
      render: (_, r) => (
        <Button
          type="text"
          size="small"
          icon={<Eye size={15} />}
          onClick={() => setDetailId(r.id)}
          data-testid="submission-detail-open"
        >
          {t('common:actions.detail')}
        </Button>
      ),
    },
  ];

  const detail = detailQ.data;
  const detailStatusValue =
    detail && (SUBMISSION_STATUS_FLOW as readonly string[]).includes(detail.status)
      ? detail.status
      : undefined;

  return (
    <div data-testid="form-submissions-page">
      <PageHeader
        title={t('submissions.title')}
        description={t('submissions.description', { title: formQ.data?.title ?? '' })}
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: t('title'), to: ROUTES.FORMS },
          { label: t('submissions.title') },
        ]}
        actions={
          <Space wrap>
            <Button icon={<ArrowLeft size={15} />} onClick={() => void navigate(ROUTES.FORMS)}>
              {t('submissions.backToForms')}
            </Button>
            <Button
              icon={<Download size={14} />}
              loading={exporting}
              onClick={() => void handleExport('xlsx')}
              data-testid="submissions-export-xlsx"
            >
              {t('submissions.export.xlsx')}
            </Button>
            <Button
              icon={<Download size={14} />}
              loading={exporting}
              onClick={() => void handleExport('pdf')}
              data-testid="submissions-export-pdf"
            >
              {t('submissions.export.pdf')}
            </Button>
          </Space>
        }
      />

      <DataTable<FormSubmissionListItem>
        data={listQ.data?.items ?? []}
        columns={columns}
        rowKey="id"
        loading={listQ.isFetching}
        emptyState={<div style={{ padding: 24, textAlign: 'center' }}><Text type="secondary">{t('submissions.empty')}</Text></div>}
        pagination={{
          page,
          size: pageSize,
          total: listQ.data?.total ?? 0,
          onChange: (p, s) => {
            setPage(p);
            setPageSize(s);
          },
        }}
        filters={
          <Space wrap>
            <Select<SubmissionStatus>
              allowClear
              placeholder={t('submissions.filters.allStatuses')}
              value={status}
              onChange={(v) => {
                setStatus(v);
                setPage(1);
              }}
              style={{ minWidth: 170 }}
              options={SUBMISSION_STATUSES.map((s) => ({ value: s, label: td(`submissions.status.${s}`) }))}
              data-testid="submissions-status-filter"
            />
            <RangePicker
              value={range}
              onChange={(v) => {
                setRange(v);
                setPage(1);
              }}
              showTime
            />
          </Space>
        }
      />

      <Drawer
        title={t('submissions.detail.title')}
        open={!!detailId}
        onClose={() => setDetailId(null)}
        width={520}
        data-testid="submission-detail-drawer"
      >
        {detailQ.isLoading || !detail ? (
          <Skeleton active />
        ) : (
          <Space direction="vertical" size={14} style={{ width: '100%' }}>
            {detail.status === 'Quarantined' ? (
              <Alert type="error" showIcon message={t('submissions.detail.quarantinedWarning')} />
            ) : null}

            <div>
              <Text type="secondary">{t('submissions.detail.submittedAt')}</Text>
              <div>{dayjs(detail.submittedAt).format('DD MMMM YYYY HH:mm:ss')}</div>
            </div>

            <div>
              <Text type="secondary">{t('submissions.detail.status')}</Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                <Tag color={STATUS_COLOR[detail.status]}>{td(`submissions.status.${detail.status}`)}</Tag>
                <Select<SubmissionStatus>
                  size="small"
                  style={{ minWidth: 190 }}
                  placeholder={t('submissions.statusUpdate.label')}
                  value={detailStatusValue}
                  loading={statusMutation.isPending}
                  onChange={(next) => statusMutation.mutate({ submissionId: detail.id, next })}
                  options={SUBMISSION_STATUS_FLOW.map((s) => ({ value: s, label: td(`submissions.status.${s}`) }))}
                  data-testid="submission-status-select"
                />
              </div>
            </div>

            {detail.submitterIp ? (
              <div>
                <Text type="secondary">{t('submissions.detail.ip')}</Text>
                <div style={{ fontFamily: 'var(--ds-font-mono, monospace)', fontSize: 12 }}>{detail.submitterIp}</div>
              </div>
            ) : null}

            {detail.kvkkConsentAt ? (
              <div>
                <Text type="secondary">{t('submissions.detail.kvkkConsent')}</Text>
                <div>{dayjs(detail.kvkkConsentAt).format('DD.MM.YYYY HH:mm')}</div>
              </div>
            ) : null}

            {detail.fileIds.length > 0 ? (
              <div>
                <Text type="secondary">{t('submissions.detail.attachments', { count: detail.fileIds.length })}</Text>
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                  {detail.fileIds.map((fid, index) => (
                    <Button
                      key={fid}
                      size="small"
                      icon={<Download size={13} />}
                      loading={downloadingFileId === fid}
                      disabled={detail.status === 'Quarantined'}
                      onClick={() => void handleDownloadFile(fid, index)}
                      data-testid="submission-file-download"
                    >
                      {t('submissions.detail.attachmentItem', { index: index + 1 })}
                    </Button>
                  ))}
                </div>
                {detail.status === 'Quarantined' ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('submissions.detail.attachmentsBlocked')}
                  </Text>
                ) : null}
              </div>
            ) : null}

            <div>
              <Text type="secondary">{t('submissions.detail.data')}</Text>
              <div style={{ marginTop: 4 }}>
                {Object.keys(detail.data).length === 0 ? (
                  <Text type="secondary">{t('submissions.detail.noData')}</Text>
                ) : (
                  Object.entries(detail.data).map(([key, value]) => (
                    <div key={key} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 12, color: 'var(--ds-text-tertiary)' }}>
                        {fieldLabels.get(key) ?? key}
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{formatValue(value, td)}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </Space>
        )}
      </Drawer>
    </div>
  );
}

const MIME_EXT: Record<string, string> = {
  'application/pdf': '.pdf',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/webp': '.webp',
};

/** Blob MIME'inden indirilen dosya adı için uzantı türetir; bilinmiyorsa uzantısız. */
function extFromMime(mime: string): string {
  return MIME_EXT[mime.split(';')[0]?.trim() ?? ''] ?? '';
}

function stringifyScalar(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'bigint') return String(value);
  return JSON.stringify(value);
}

function formatValue(value: unknown, t: (key: string) => string): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? t('submissions.detail.yes') : t('submissions.detail.no');
  if (Array.isArray(value)) return value.map((v) => stringifyScalar(v)).join(', ');
  return stringifyScalar(value);
}
